// 앱 안에서 현장 코드로 연결한 공사 목록 — 이 기기에만 · 앱 첫 화면(/start)이 읽는다 · 저장소가 막히거나 값이 깨지면 없는 셈 친다.

import type { DraftStore } from "@/modules/project/ui/log-draft";

export const CUSTOMER_LINKS_KEY = "buildpy:customer-links";
export const CUSTOMER_LINKS_MAX = 10;

export interface CustomerLink {
  token: string;
  path: string;
  orgName: string;
  customerName: string;
  savedAt: number;
}

type LinkStore = Pick<DraftStore, "getItem" | "setItem">;

const textOf = (value: unknown): string => (typeof value === "string" ? value : "");

// 고객 공사 화면(/c/<토큰>/project·house)만 — 저장소를 고쳐 밖이나 `..` 로 보내지 못하게 한다.
const CUSTOMER_PATH = /^\/c\/[A-Za-z0-9_-]+\/(project|house)$/;
const isCustomerPath = (path: string): boolean => CUSTOMER_PATH.test(path);

const linkOf = (raw: unknown): CustomerLink | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const token = textOf(value.token);
  const path = textOf(value.path);
  const savedAt = typeof value.savedAt === "number" && Number.isFinite(value.savedAt) ? value.savedAt : 0;
  if (!token || !isCustomerPath(path)) return null;
  return { token, path, orgName: textOf(value.orgName), customerName: textOf(value.customerName), savedAt };
};

// 깨진 줄은 빼고 · 같은 token 은 최근 것 하나 · 최근순 · 상한까지.
export const normalizeCustomerLinks = (raw: unknown): CustomerLink[] => {
  if (!Array.isArray(raw)) return [];
  const sorted = raw
    .map(linkOf)
    .filter((link): link is CustomerLink => link !== null)
    .sort((a, b) => b.savedAt - a.savedAt);
  const seen = new Set<string>();
  return sorted.filter((link) => !seen.has(link.token) && seen.add(link.token)).slice(0, CUSTOMER_LINKS_MAX);
};

export const readCustomerLinks = (store: LinkStore | null): CustomerLink[] => {
  if (!store) return [];
  try {
    const raw = store.getItem(CUSTOMER_LINKS_KEY);
    return raw ? normalizeCustomerLinks(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

const writeLinks = (store: LinkStore, next: CustomerLink[]): CustomerLink[] | null => {
  try {
    store.setItem(CUSTOMER_LINKS_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
};

// 맨 앞에 넣고(같은 token 은 갱신) 저장한 목록을 돌려준다 · 못 쓰면 null.
export const rememberCustomerLink = (store: LinkStore | null, link: CustomerLink): CustomerLink[] | null =>
  store && writeLinks(store, normalizeCustomerLinks([link, ...readCustomerLinks(store).filter((saved) => saved.token !== link.token)]));

// 죽은 링크(회수·만료·재발급으로 바뀐 옛 토큰)를 잊는다 — 남은 목록 · 못 쓰면 null.
export const forgetCustomerLink = (store: LinkStore | null, token: string): CustomerLink[] | null =>
  store && writeLinks(store, readCustomerLinks(store).filter((saved) => saved.token !== token));
